import React, { useState } from 'react';
import {
  Button,
  Container,
  DatePicker,
  Form,
  FormField,
  Header,
  Input,
  Modal,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { generateGlueArnForPrefixLookupFromId } from 'src/components/utils/arnUtil';
import { ResourceSelector } from 'src/components/common/resourceSelector/resourceSelector';
import { Link } from 'src/components/common/Link';
import { StepErrors } from 'src/components/auditReports/createReportPage/createReportWizard';

export interface AccessGrantConfigEditorProps {
  activeGroup: string;
  activeWorkspace?: any;
  accessProvider: string;
  setAccessProvider: any;
  governanceTag: string;
  setGovernanceTag: any;
  resourceArn: string;
  setResourceArn: any;
  status: string;
  setStatus: any;
  startDate: string;
  setStartDate: any;
  endDate: string;
  setEndDate: any;
  displayAccessProviderFilter: boolean;
  displayDecisionDateRange: boolean;
  stepErrors: StepErrors;
  validateResourceArnStepField: any;
}

export const CREATE_REPORT_WIKI_PAGE =
  'https://w.amazon.com/bin/view/AWSDataGovernanceServices/Omni/Features/#HAuditreport';
export const REP_RESOURCE_ARN_REGEX =
  /^arn:aws:(glue|redshift):(us-east-1|us-west-2):\d{12}(:)?(cluster(\/[\w-]+)?)?(bucket(\/[\w-]+)?)?((\/)?database(\/[\w-]+)?(\/table(\/[\w-]+)?)?)?$/;

export const resourceArnIsValid = (resourceArn: string) => {
  // can be empty or undefined
  if (!resourceArn || resourceArn.trim() === '') {
    return true;
  }
  // If present, should match any pattern with prefix here:
  // https://code.amazon.com/packages/AWSD3AService/blobs/mainline/--/src/main/java/com/amazon/awsd3aservice/helper/ArnUtil.java
  return REP_RESOURCE_ARN_REGEX.test(resourceArn);
};

export const AccessGrantConfigEditor = (props: AccessGrantConfigEditorProps) => {
  const [resourceSelectorModalVisible, setResourceSelectorModalVisible] = useState<boolean>(false);
  const [resourceModalResourceId, setResourceModalResourceId] = useState<string>(undefined);

  const ACCESS_PROVIDER_OPTIONS: OptionDefinition[] = [
    { label: '-' },
    { label: 'Omni', value: 'Omni' },
    { label: 'Galaxi', value: 'GAM' },
    { label: 'Wizard', value: 'Wizard' },
    { label: 'DataHub', value: 'DataHub' },
  ];
  const GOVERNANCE_TAG_OPTIONS: OptionDefinition[] = [
    { label: '-' },
    { label: 'AWS Marketplace', value: 'AWSMarketplace' },
    { label: 'Personal Data', value: 'Personal Data' },
  ];
  const STATUS_OPTIONS: OptionDefinition[] = [
    // "Active:Approved", "Inactive:Revoked", "Inactive:Relinquished", "Inactive:Deleted"
    { label: '-' },
    { label: 'Active:Approved', value: 'Active:Approved' },
    { label: 'Inactive:Revoked', value: 'Inactive:Revoked' },
    { label: 'Inactive:Relinquished', value: 'Inactive:Relinquished' },
    { label: 'Inactive:Deleted', value: 'Inactive:Deleted' },
  ];

  const YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;
  const ISO_8601_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
  const dateFormatToIso = (dateFormatString: string) => {
    if (!YYYY_MM_DD_REGEX.test(dateFormatString)) return '';

    const [year, month, day] = dateFormatString.split('-');
    return `${year}-${month}-${day}T00:00:00Z`;
  };
  const isoToDateFormat = (isoString: string) => {
    if (!ISO_8601_REGEX.test(isoString)) return '';

    const [year, month, day] = isoString.split('-');
    return `${year}-${month}-${day}`;
  };
  const createdOnDateErrorText = () => {
    if (props.startDate && props.endDate && props.startDate >= props.endDate) {
      return 'Start date must be before end date.';
    }
  };

  const setResourceArnFromResourceSelectorId = () => {
    const arn: string = generateGlueArnForPrefixLookupFromId(resourceModalResourceId);
    props.setResourceArn(arn);
    setResourceSelectorModalVisible(false);
  };

  return (
    <Form>
      {resourceSelectorModalVisible && (
        <Modal
          visible={resourceSelectorModalVisible}
          onDismiss={() => setResourceSelectorModalVisible(false)}
          header={<Header>Select resource</Header>}
          footer={
            <Header
              actions={
                <SpaceBetween size={'xs'} direction={'horizontal'}>
                  <Button variant={'link'} onClick={() => setResourceSelectorModalVisible(false)}>
                    Cancel
                  </Button>
                  <Button onClick={setResourceArnFromResourceSelectorId}>Select</Button>
                </SpaceBetween>
              }
            />
          }
        >
          <ResourceSelector
            {...props}
            setNotification={() => {}}
            onSelect={(id: string) => {
              setResourceModalResourceId(id);
            }}
            disableGroupAndWorkspaceSelection={true}
            disableMetadataFormSelection={true}
            disableGovernanceTagSelection={true}
            disableTagSelection={true}
            disableCatalogSelection={false}
            disableFgaSelection={true}
            disableRedshiftSelection={true}
          />
        </Modal>
      )}
      <SpaceBetween size={'m'} direction={'vertical'}>
        {props.displayAccessProviderFilter && (
          <FormField
            label={
              <span>
                Access provider <i>- optional</i>
              </span>
            }
            description={
              'Filter access grants by a specific access provider. If none is selected, all access providers will be used.'
            }
          >
            <Select
              options={ACCESS_PROVIDER_OPTIONS}
              selectedOption={ACCESS_PROVIDER_OPTIONS.find((option) => option.value === props.accessProvider)}
              onChange={(e) => props.setAccessProvider(e.detail.selectedOption.value)}
            />
          </FormField>
        )}
        <FormField
          label={
            <span>
              Governance tag <i>- optional</i>
            </span>
          }
          description={
            'Filter access grants by a specific governance tag. If none is selected, all governance tags will be used.'
          }
        >
          <Select
            options={GOVERNANCE_TAG_OPTIONS}
            selectedOption={GOVERNANCE_TAG_OPTIONS.find((option) => option.value === props.governanceTag)}
            onChange={(e) => props.setGovernanceTag(e.detail.selectedOption.value)}
          />
        </FormField>
        <FormField
          label={
            <span>
              Access grant status <i>- optional</i>
            </span>
          }
          description={'Filter access grants by their status.'}
        >
          <Select
            options={STATUS_OPTIONS}
            selectedOption={STATUS_OPTIONS.find((option) => option.value === props.status)}
            onChange={(e) => props.setStatus(e.detail.selectedOption.value)}
          />
        </FormField>
        <FormField
          label={
            <span>
              Resource ARN prefix <i>- optional</i>
            </span>
          }
          description={
            'If you would like to report on access grants only for a specific data resource, provide the ARN here. ' +
            'Provided ARN is matched with the prefix of the actual ARNs. Leave blank to include access grants for all resources.'
          }
          info={
            <Link variant={'info'} external={true} href={CREATE_REPORT_WIKI_PAGE}>
              Info
            </Link>
          }
          errorText={props.stepErrors?.resourceArn}
        >
          <SpaceBetween size={'s'} direction={'vertical'}>
            <Button onClick={() => setResourceSelectorModalVisible(true)}>Select resource</Button>
            <Input
              value={props.resourceArn}
              placeholder={'arn:aws:glue:us-east-1:123456789012:database/database_name/table/table_name'}
              onChange={(e) => {
                props.setResourceArn(e.detail.value);
                props.validateResourceArnStepField(e.detail.value);
              }}
              invalid={!resourceArnIsValid(props.resourceArn)}
            />
          </SpaceBetween>
        </FormField>
        {props.displayDecisionDateRange && (
          <Container
            header={
              <Header
                variant={'h2'}
                description={
                  'Select a date range to include access grants that were decided (approved or denied) within that range.'
                }
              >
                Access grant decision date range
              </Header>
            }
          >
            <SpaceBetween size={'l'} direction={'vertical'}>
              <FormField
                label={
                  <span>
                    Start date <i>- optional</i>
                  </span>
                }
                description={'Select a date to only include access grants decided after that date, or leave blank.'}
              >
                <DatePicker
                  onChange={async (e) => props.setStartDate(dateFormatToIso(e.detail.value))}
                  value={isoToDateFormat(props.startDate)}
                  invalid={!!createdOnDateErrorText()}
                  placeholder='YYYY/MM/DD'
                />
              </FormField>
              <FormField
                label={
                  <span>
                    End date <i>- optional</i>
                  </span>
                }
                description={'Select a date to only include access grants decided before that date, or leave blank.'}
                errorText={createdOnDateErrorText()}
              >
                <DatePicker
                  onChange={async (e) => props.setEndDate(dateFormatToIso(e.detail.value))}
                  value={isoToDateFormat(props.endDate)}
                  placeholder='YYYY/MM/DD'
                />
              </FormField>
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Form>
  );
};
